import { useReactiveVar } from '@apollo/client';
import { ReactNode, useEffect, useMemo } from 'react';
import { useSearchParam } from 'react-use';

import { isDevelopmentMode } from '@/utils/environmentUtils';

import {
  DebugContext,
  DebugContextShape,
  DebugLevel,
  debugLevelVar,
} from './debug.context';

export interface DebugContextProviderProps {
  children: ReactNode;
}

const validDebugLevels = new Set(Object.values(DebugLevel));

function parseDebugLevel(debugParam: string | null): DebugLevel | null {
  if (debugParam === null) {
    return isDevelopmentMode() ? DebugLevel.VERBOSE : null;
  }
  const level = parseInt(debugParam as unknown as string, 10);
  if (Number.isInteger(level) && validDebugLevels.has(level)) {
    return level as DebugLevel;
  }

  return isDevelopmentMode() ? DebugLevel.VERBOSE : null;
}

export const DebugContextProvider = ({
  children,
}: DebugContextProviderProps) => {
  const debug = useSearchParam('debug');

  // Update the reactive variable whenever the search param changes
  useEffect(() => {
    const newLevel = parseDebugLevel(debug);
    debugLevelVar(newLevel);
  }, [debug]);

  // Read from the reactive var for the context value
  const currentDebugLevel = useReactiveVar(debugLevelVar);

  const contextValue = useMemo<DebugContextShape>(() => {
    return { debugLevel: currentDebugLevel };
  }, [currentDebugLevel]);

  return (
    <DebugContext.Provider value={contextValue}>
      {children}
    </DebugContext.Provider>
  );
};
